import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";

export interface Personals {
	id: number;
	user_id?: number;

	title: string;
	description?: string | null;

	start_date: string;
	end_date?: string | null;

	start_time?: string | null;
	end_time?: string | null;

	all_day: boolean;

	location?: string | null;
	recurrence_rule?: string | null;

	created_at?: string;
	updated_at?: string;
}

interface PersonalsState {
	items: Personals[];
	selectedPersonal: Personals | null;
	loading: boolean;
	error: string | null;
}

const initialState: PersonalsState = {
	items: [],
	selectedPersonal: null,
	loading: false,
	error: null,
};

export const personalsSlice = createSlice({
	name: "personals",
	initialState,
	reducers: {
		setPersonals: (state, action: PayloadAction<Personals[]>) => {
			state.items = action.payload;
		},

		addPersonal: (state, action: PayloadAction<Personals>) => {
			state.items.push(action.payload);
		},

		updatePersonal: (
			state,
			action: PayloadAction<{
				id: number;
				updates: Partial<Personals>;
			}>,
		) => {
			const { id, updates } = action.payload;
			const personal = state.items.find((item) => item.id === id);

			if (personal) {
				Object.assign(personal, updates);
			}
		},

		removePersonal: (state, action: PayloadAction<number>) => {
			state.items = state.items.filter(
				(item) => item.id !== action.payload,
			);
		},

		setSelectedPersonal: (
			state,
			action: PayloadAction<Personals | null>,
		) => {
			state.selectedPersonal = action.payload;
		},

		clearSelectedPersonal: (state) => {
			state.selectedPersonal = null;
		},

		setPersonalsLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},

		setPersonalsError: (state, action: PayloadAction<string | null>) => {
			state.error = action.payload;
		},

		clearPersonals: (state) => {
			state.items = [];
			state.loading = false;
			state.error = null;
		},
	},
});

export const {
	setPersonals,
	addPersonal,
	updatePersonal,
	removePersonal,
	setSelectedPersonal,
	clearSelectedPersonal,
	setPersonalsLoading,
	setPersonalsError,
	clearPersonals,
} = personalsSlice.actions;

export const selectPersonals = (state: RootState) => state.personals.items;
export const selectSelectedPersonal = (state: RootState) =>
	state.personals.selectedPersonal;
export const selectPersonalsLoading = (state: RootState) =>
	state.personals.loading;
export const selectPersonalsError = (state: RootState) => state.personals.error;

export default personalsSlice.reducer;
